import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { idempotencyKey, type ApiErrorInfo } from "@/lib/api";
import { NETWORK_ERROR_CODE, createInvoiceDraft, issueInvoice, updateInvoiceDraft } from "@/features/invoicing/api";
import { formatMoney, type Money } from "@/features/invoicing/money";
import type {
  CreateInvoiceRequest,
  Invoice,
  InvoicingSettings,
  IssueInvoiceResponse,
  NetworkLimit,
  UpdateInvoiceRequest,
} from "@/features/invoicing/types";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { EmptyState } from "@/components/ui/feedback";
import { InfoBanner } from "@/components/ui/banner";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { assetCatalog } from "./assets";
import { COPY } from "./copy";
import { LineEditor } from "./line-editor";
import {
  checkNetworkFloor,
  isPastDueDate,
  previewInvoice,
  todayIsoDate,
  type DraftLine,
} from "./lines";
import { RefusalPanel } from "./refusal-panel";
import { PayerPicker } from "./payer-picker";
import type { PayersResult } from "./use-payers";
import type { InvoiceScopes } from "./use-invoice-scopes";

/**
 * The create form (`/stablecoins/invoices/new`, page spec `invoice-create`).
 *
 * ## What it does, in the contract's own order
 *
 * `POST /v1/invoices` creates a **draft** only: no number, no address and no
 * pay link exist until `POST /v1/invoices/{id}/issue`. The form is built around
 * that two-step rather than hiding it:
 *
 *  1. **Save draft** — creates the draft. If a draft already exists and the
 *     lines, due date or memo changed, the same button patches it through
 *     `PATCH /v1/invoices/{id}` with `If-Match: <the version this page read>`.
 *  2. **Issue** — enabled only once a draft exists and has no unsaved changes,
 *     because issuing acts on the *stored* draft, not on what is on screen.
 *
 * The payer and the asset are **locked once the draft exists**: the contract's
 * draft update carries `lines`, `taxIds`, `dueDate` and `memo` only. A control
 * that looked editable but could not be saved would be worse than one that is
 * plainly fixed, so the reason is on the page (`COPY.lockedNote`).
 *
 * An unchanged save or issue intent retains one `Idempotency-Key` until its
 * successful reply. A failed response may have followed a committed write, so
 * retrying must reuse that key. Changed input/version, success or Start over
 * ends the intent; the next request receives a fresh key.
 *
 * ## What it deliberately does not do
 *
 *  - **No approve or reject control, ever** — not for a human, not for a
 *    machine principal. The page says so in prose rather than rendering a
 *    disabled button, so a machine principal is never shown an affordance it
 *    can never have (`MACHINE_PRINCIPAL_NOT_AN_APPROVER`).
 *  - **No client-side authority.** The payer, the asset, the network floors and
 *    the due date are the service's to refuse; the form warns, and renders the
 *    refusal it gets back, verbatim.
 *  - **No invented numbers.** Totals come from `previewInvoice` over the typed
 *    strings and the asset's `decimals` as declared by the settings response.
 *    If a symbol has no declared decimal count it is not offered at all
 *    (`assets.ts`), because guessing a scale is guessing an amount.
 */

interface WriteIntent {
  signature: string;
  key: string;
}

/** Request keys live only in this form session; they are never logged or persisted. */
function requestIntentKey(ref: { current: WriteIntent | null }, scope: string, signature: string): string {
  const previous = ref.current;
  if (previous && previous.signature === signature) return previous.key;
  const key = idempotencyKey(scope);
  ref.current = { signature, key };
  return key;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function addDays(isoDate: string, days: number): string {
  if (!ISO_DATE.test(isoDate)) return "";
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export interface InvoiceFormProps {
  settings: InvoicingSettings;
  payers: PayersResult;
  scopes: InvoiceScopes;
  onIssued: (invoice: IssueInvoiceResponse) => void;
}

export function InvoiceForm({ settings, payers, scopes, onIssued }: InvoiceFormProps) {
  const catalog = useMemo(() => assetCatalog(settings), [settings]);
  const today = useMemo(() => todayIsoDate(), []);

  const [payerId, setPayerId] = useState("");
  const [assetSymbol, setAssetSymbol] = useState(() => catalog.assets[0]?.symbol ?? "");
  const [network, setNetwork] = useState(() => catalog.assets[0]?.networks[0] ?? "");
  const [rows, setRows] = useState<DraftLine[]>([]);
  const [dueDate, setDueDate] = useState(() => addDays(today, settings.defaultTermsDays));
  const [memo, setMemo] = useState("");

  const [draft, setDraft] = useState<Invoice | null>(null);
  /** The body the stored draft was last saved from. "Dirty" is a fact, not a guess. */
  const [savedBody, setSavedBody] = useState<string | null>(null);
  const [busy, setBusy] = useState<"none" | "saving" | "issuing">("none");
  const [refusal, setRefusal] = useState<ApiErrorInfo | null>(null);
  const saveIntent = useRef<WriteIntent | null>(null);
  const issueIntent = useRef<WriteIntent | null>(null);

  const asset = catalog.assets.find((entry) => entry.symbol === assetSymbol) ?? null;
  const preview = useMemo(
    () => (asset ? previewInvoice(rows, asset) : null),
    [rows, asset],
  );

  const lines = preview ? preview.lineInputs : [];
  const linesReady = preview !== null && preview.complete;

  /** The body `POST /v1/invoices` takes: payer and asset included. */
  const createBody: CreateInvoiceRequest | null =
    asset && payerId && linesReady
      ? {
          payerId,
          assetSymbol: asset.symbol,
          lines,
          ...(dueDate ? { dueDate } : {}),
          ...(memo.trim() ? { memo: memo.trim() } : {}),
        }
      : null;

  /** The body `PATCH /v1/invoices/{id}` takes: no payer, no asset — the contract does not carry them. */
  const updateBody: UpdateInvoiceRequest | null = linesReady
    ? {
        lines,
        ...(dueDate ? { dueDate } : {}),
        ...(memo.trim() ? { memo: memo.trim() } : {}),
      }
    : null;

  /**
   * "Dirty" is measured on the **update** body — the fields a stored draft
   * actually has — not on whichever body the next save would send. Comparing
   * against the create body instead would report every draft as dirty the
   * instant it was saved, because the create body carries a payer and an asset
   * the update body does not.
   */
  const updateBodyJson = updateBody ? JSON.stringify(updateBody) : null;
  const dirty = draft !== null && updateBodyJson !== null && updateBodyJson !== savedBody;

  const limits = settings.networkLimits ?? [];
  const floors = preview && asset ? checkNetworkFloor(preview.total.amount, limits, network) : null;
  const dueDatePast = isPastDueDate(dueDate, today);
  const expiresAt = dueDate ? addDays(dueDate, settings.expiryGraceDays) : "";

  const missing: string[] = [];
  if (!payerId) missing.push(COPY.payerRequired);
  if (rows.length === 0) missing.push(COPY.linesRequired);
  if (preview?.totalBelowZero) missing.push(COPY.discountExceedsExtras);
  else if (preview && rows.length > 0 && !preview.complete) missing.push(COPY.fixLines);

  const hasBodyToSave = draft === null ? createBody !== null : updateBody !== null && dirty;
  const canSave = hasBodyToSave && scopes.canCreate && busy === "none";
  const canIssue = draft !== null && !dirty && scopes.canIssue && busy === "none" && linesReady;

  const saveNote = !scopes.loaded
    ? COPY.scopeUnknown
    : !scopes.canCreate
      ? COPY.scopeMissingCreate
      : null;
  const issueNote = !scopes.canIssue
    ? COPY.scopeMissingIssue
    : draft === null
      ? COPY.issueRequiresDraft
      : null;

  async function saveDraft() {
    if (!canSave) return;
    setBusy("saving");
    setRefusal(null);
    try {
      const scope = draft === null ? "invoicing_invoice_create" : "invoicing_invoice_update";
      const key = requestIntentKey(saveIntent, scope, JSON.stringify([
        scope, draft?.invoiceId ?? null, draft?.version ?? null,
        draft === null ? createBody : updateBody,
      ]));
      const result =
        draft === null
          ? createBody &&
            (await createInvoiceDraft(createBody, { idempotencyKey: key }))
          : updateBody &&
            (await updateInvoiceDraft(draft.invoiceId, updateBody, {
              ifMatch: draft.version,
              idempotencyKey: key,
            }));
      if (!result) return;
      if (!result.ok) {
        setRefusal(result.error);
        return;
      }
      saveIntent.current = null;
      setDraft(result.data);
      setSavedBody(updateBodyJson);
    } catch {
      setRefusal({ reasonCode: NETWORK_ERROR_CODE });
    } finally {
      setBusy("none");
    }
  }

  async function issue() {
    if (!draft || !canIssue) return;
    setBusy("issuing");
    setRefusal(null);
    try {
      const result = await issueInvoice(draft.invoiceId, {
        ifMatch: draft.version,
        idempotencyKey: requestIntentKey(issueIntent, "invoicing_invoice_issue", JSON.stringify([draft.invoiceId, draft.version])),
      });
      if (!result.ok) {
        setRefusal(result.error);
        return;
      }
      issueIntent.current = null;
      onIssued(result.data);
    } catch {
      setRefusal({ reasonCode: NETWORK_ERROR_CODE });
    } finally {
      setBusy("none");
    }
  }

  function startOver() {
    saveIntent.current = null;
    issueIntent.current = null;
    setDraft(null);
    setSavedBody(null);
    setRows([]);
    setPayerId("");
    setMemo("");
    setDueDate(addDays(today, settings.defaultTermsDays));
    setRefusal(null);
  }

  /*
   * No asset to denominate in means there is nothing to compose. The form is
   * not rendered with zero assets and a totals block reading 0: that figure
   * would be a total nobody computed.
   */
  if (!asset || catalog.assets.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <EmptyState
          card
          title={COPY.assetNone}
          description={COPY.assetNoneDescription}
          action={
            <Link className="text-sm font-semibold text-primary underline" to="/stablecoins/invoices/settings">
              {COPY.assetNoneAction}
            </Link>
          }
        />
        {catalog.unpricedSymbols.length > 0 && (
          <p className="text-xs text-warning">
            {catalog.unpricedSymbols.join(", ")} — no decimal count is declared for this asset in the settings
            response, so it cannot be offered: the amount's scale would have to be guessed.
          </p>
        )}
      </div>
    );
  }

  const locked = draft !== null;

  return (
    <div className="flex flex-col gap-5">
      {/* ------------------------------------------------------------- payer */}
      <section aria-labelledby="invoice-payer-heading" className="rounded-lg border border-line bg-surface p-4 sm:p-5">
        <h2 id="invoice-payer-heading" className="mb-3.5 text-sm font-bold text-ink">
          {COPY.payerHeading}
        </h2>
        <PayerPicker
          payers={payers.payers}
          loading={payers.loading}
          error={payers.error}
          selectedId={payerId}
          onSelect={setPayerId}
          onCreate={payers.createPayer}
          creating={payers.creating}
          canManagePayers={scopes.canManagePayers && !locked}
          scopeNote={
            locked
              ? COPY.lockedNote
              : scopes.canManagePayers
                ? null
                : scopes.loaded
                  ? COPY.scopeMissingPayer
                  : COPY.scopeUnknown
          }
        />
      </section>

      {/* --------------------------------------------------- asset & network */}
      <section aria-labelledby="invoice-asset-heading" className="rounded-lg border border-line bg-surface p-4 sm:p-5">
        <h2 id="invoice-asset-heading" className="mb-3.5 text-sm font-bold text-ink">
          {COPY.detailsHeading}
        </h2>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={COPY.assetLabel} htmlFor="invoice-asset" hint={COPY.assetHint} required>
            <Select
              id="invoice-asset"
              className="w-full"
              value={assetSymbol}
              disabled={locked}
              onChange={(event) => {
                const next = event.target.value;
                setAssetSymbol(next);
                setNetwork(catalog.assets.find((entry) => entry.symbol === next)?.networks[0] ?? "");
              }}
            >
              {catalog.assets.map((entry) => (
                <option key={entry.symbol} value={entry.symbol}>
                  {entry.symbol}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={COPY.networkLabel} htmlFor="invoice-network" hint={COPY.networkHint} required>
            <Select
              id="invoice-network"
              className="w-full"
              value={network}
              disabled={asset.networks.length === 0}
              onChange={(event) => setNetwork(event.target.value)}
            >
              {asset.networks.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="mt-3 flex flex-col gap-1 text-xs text-muted">
          <p data-testid="invoice-accepted-networks">
            <span className="font-semibold">{COPY.networkAcceptsPrefix}</span> {asset.networks.join(", ")}
          </p>
          <NetworkFloorNote label={COPY.networkFloorPrefix} limits={limits} network={network} field="minInvoiceAmount" />
          <NetworkFloorNote label={COPY.networkDustPrefix} limits={limits} network={network} field="dustThreshold" />
        </div>
        {locked && <p className="mt-3 text-xs text-muted">{COPY.lockedNote}</p>}
      </section>

      {/* ------------------------------------------------------------- lines */}
      <section aria-labelledby="invoice-lines-heading" className="rounded-lg border border-line bg-surface p-4 sm:p-5">
        <h2 id="invoice-lines-heading" className="mb-3.5 text-sm font-bold text-ink">
          {COPY.linesHeading}
        </h2>
        <LineEditor
          rows={rows}
          priced={preview?.lines ?? []}
          asset={asset}
          onChange={setRows}
          disabled={busy !== "none"}
        />
      </section>

      {/* ------------------------------------------------------------- terms */}
      <section aria-labelledby="invoice-terms-heading" className="rounded-lg border border-line bg-surface p-4 sm:p-5">
        <h2 id="invoice-terms-heading" className="mb-3.5 text-sm font-bold text-ink">
          {COPY.termsHeading}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={COPY.dueDateLabel} htmlFor="invoice-due-date" hint={COPY.dueDateHint}>
            <div className="flex items-center gap-2">
              <Input
                id="invoice-due-date"
                type="date"
                min={today}
                value={dueDate}
                error={dueDatePast}
                onChange={(event) => setDueDate(event.target.value)}
              />
              {dueDate && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setDueDate("")}>
                  {COPY.dueDateClear}
                </Button>
              )}
            </div>
          </Field>
          <Field label={COPY.memoLabel} htmlFor="invoice-memo" hint={COPY.memoHint}>
            <Input id="invoice-memo" value={memo} maxLength={280} onChange={(event) => setMemo(event.target.value)} />
          </Field>
        </div>
        {dueDatePast && (
          <p className="mt-2 text-xs text-danger" role="alert">
            {COPY.dueDatePast}
          </p>
        )}
        {expiresAt && (
          <p className="mt-2 text-xs text-muted">
            {COPY.issuedExpiry}: {expiresAt}
          </p>
        )}
      </section>

      {/* ------------------------------------------------------------ totals */}
      <section aria-labelledby="invoice-totals-heading" className="rounded-lg border border-line bg-surface p-4 sm:p-5">
        <h2 id="invoice-totals-heading" className="mb-3.5 text-sm font-bold text-ink">
          {COPY.totalsHeading}
        </h2>
        <dl className="flex flex-col gap-1.5 text-sm" data-testid="invoice-totals">
          {preview && (
            <>
              <TotalRow label={COPY.subtotal} value={preview.subtotal} />
              <TotalRow label={COPY.tax} value={preview.tax} />
              <TotalRow label={COPY.discount} value={preview.discount} />
              <TotalRow label={COPY.grandTotal} value={preview.total} strong />
            </>
          )}
        </dl>
        {preview && (
          <p className="mt-2 text-xs text-muted" data-testid="invoice-total-base-units">
            {COPY.totalsInBaseUnits}:{" "}
            <code dir="ltr">{preview.total.amount}</code> · {COPY.totalsBaseUnitHint}
          </p>
        )}
        {floors && (
          <InfoBanner variant="warning" className="mt-3 !mb-0" title={COPY.totalsHeading}>
            <span data-testid="network-floor-note">{floors}</span>
          </InfoBanner>
        )}
      </section>

      {/* ----------------------------------------------------------- actions */}
      {refusal && <RefusalPanel error={refusal} testId="invoice-refusal" />}

      {draft && (
        <section aria-labelledby="invoice-draft-heading" className="rounded-lg border border-line bg-bg p-4 sm:p-5">
          <h2 id="invoice-draft-heading" className="text-sm font-bold text-ink">
            {COPY.draftSavedTitle}
          </h2>
          <p className="mt-1 text-sm text-muted">{COPY.draftSavedDescription}</p>
          <dl className="mt-2 flex flex-col gap-1 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <dt className="text-muted">{COPY.draftIdLabel}</dt>
              <dd>
                <IdentifierValue value={draft.invoiceId} kind="draft" copy />
              </dd>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <dt className="text-muted">{COPY.issuedNumber}</dt>
              <dd className="font-semibold text-ink">{draft.number ?? COPY.draftNumberPending}</dd>
            </div>
          </dl>
          {dirty && (
            <p className="mt-2 text-xs text-warning" data-testid="draft-dirty-note">
              {COPY.unsavedChanges}
            </p>
          )}
        </section>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" onClick={() => void saveDraft()} disabled={!canSave} loading={busy === "saving"}>
            {busy === "saving" ? COPY.savingDraft : draft ? COPY.saveDraftAgain : COPY.saveDraft}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void issue()}
            disabled={!canIssue}
            loading={busy === "issuing"}
          >
            {busy === "issuing" ? COPY.issuing : COPY.issue}
          </Button>
          {(draft || rows.length > 0) && (
            <Button type="button" variant="ghost" onClick={startOver} disabled={busy !== "none"}>
              {COPY.startOver}
            </Button>
          )}
        </div>

        {missing.length > 0 && (
          <p className="text-xs text-muted" data-testid="invoice-missing">
            {missing.join(" ")}
          </p>
        )}
        {saveNote && <p className="text-xs text-muted">{saveNote}</p>}
        {!saveNote && issueNote && <p className="text-xs text-muted">{issueNote}</p>}
        <p className="text-xs text-muted" data-testid="invoice-four-eyes-note">
          {COPY.fourEyesNote}
        </p>
      </div>
    </div>
  );
}

function TotalRow({ label, value, strong = false }: { label: string; value: Money; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className={strong ? "font-bold text-ink" : "text-muted"}>{label}</dt>
      <dd className={strong ? "font-bold text-ink" : "font-semibold text-ink"}>
        {formatMoney(value)} {value.asset.symbol}
      </dd>
    </div>
  );
}

/** One configured floor from the settings response, shown with its own units. */
function NetworkFloorNote({
  label,
  limits,
  network,
  field,
}: {
  label: string;
  limits: NetworkLimit[];
  network: string;
  field: "minInvoiceAmount" | "dustThreshold";
}) {
  const limit = limits.find((entry) => entry.network === network);
  if (!limit) return null;
  const value = limit[field];
  return (
    <p>
      <span className="font-semibold">{label}</span> {formatMoney(value)} {value.asset.symbol}
    </p>
  );
}
